import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, FilePlus, Trash, ArrowClockwise, ArrowSquareOut, Sparkle, DownloadSimple } from "@phosphor-icons/react";
import { metFirma } from "@/lib/downloadUrl";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const TYPES = [
  ["idkaart", "Identiteitskaart"],
  ["rijbewijs", "Rijbewijs"],
  ["arbeidscontract", "Arbeidscontract"],
  ["diploma", "Diploma"],
  ["medisch_attest", "Medisch attest"],
  ["verblijfskaart", "Verblijfskaart"],
  ["werkvergunning", "Werkvergunning"],
  ["bankkaart", "Bankkaart"],
  ["loonbrief", "Loonbrief"],
  ["attest", "Attest"],
  ["ander", "Ander document"],
];

const dagenTot = (datum) => {
  if (!datum) return null;
  return Math.ceil((new Date(datum) - new Date()) / 86400000);
};

export const GeldigheidBadge = ({ datum }) => {
  const d = dagenTot(datum);
  if (d === null) return null;
  const tekst = new Date(datum).toLocaleDateString("nl-BE");
  if (d < 0) return <Badge className="bg-[#DC2626] text-white text-[9px] font-bold">VERVALLEN {tekst}</Badge>;
  if (d <= 60) return <Badge className="bg-[#F59E0B] text-white text-[9px] font-bold">TOT {tekst}</Badge>;
  return <span className="text-[10px] text-muted-foreground">geldig tot {tekst}</span>;
};

function FotoSlot({ medewerkerId, soort, kant, aanwezig, onVerwijderd }) {
  const pad = soort === "eid" ? "eid-foto" : "rijbewijs-foto";
  const label = `${soort === "eid" ? "ID" : "Rijbewijs"} ${kant === "voor" ? "voorzijde" : "achterzijde"}`;
  if (!aanwezig) {
    return (
      <div className="w-[92px] shrink-0" data-testid={`fotoslot-leeg-${soort}-${kant}`}>
        <div className="h-[58px] rounded-sm border border-dashed border-border bg-muted/30 flex items-center justify-center">
          <Camera size={16} className="text-muted-foreground" />
        </div>
        <p className="text-[9px] text-muted-foreground mt-1 leading-tight">{label}</p>
      </div>
    );
  }
  const src = metFirma(`${API}/contracten/${pad}/${medewerkerId}/${kant}`);
  return (
    <div className="w-[92px] shrink-0" data-testid={`fotoslot-${soort}-${kant}`}>
      <a href={src} target="_blank" rel="noreferrer" title={`${label} openen`}>
        <img
          src={src}
          alt={label}
          className="h-[58px] w-full object-cover rounded-sm border border-border hover:border-[#002FA7] transition-colors"
        />
      </a>
      <div className="flex items-center justify-between mt-1">
        <p className="text-[9px] text-muted-foreground leading-tight">{label}</p>
        <button
          type="button"
          onClick={async () => {
            if (!window.confirm(`${label} verwijderen?`)) return;
            try {
              await axios.delete(`${API}/contracten/${pad}/${medewerkerId}/${kant}`);
              toast.success("Foto verwijderd");
              onVerwijderd(soort, kant);
            } catch {
              toast.error("Verwijderen mislukt");
            }
          }}
          className="text-[#DC2626] hover:opacity-70"
          title="Verwijderen"
          data-testid={`fotoslot-del-${soort}-${kant}`}
        >
          <Trash size={11} weight="bold" />
        </button>
      </div>
    </div>
  );
}

export default function DocumentenKluis({ medewerkerId, form, onFotoVerwijderd, onAiGegevens }) {
  const [docs, setDocs] = useState([]);
  const [bezig, setBezig] = useState(false);
  const cameraRef = useRef(null);
  const fileRef = useRef(null);

  const laden = useCallback(async () => {
    if (!medewerkerId) return;
    try {
      const res = await axios.get(`${API}/documenten`, { params: { medewerker_id: medewerkerId } });
      setDocs(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Documenten laden mislukt");
    }
  }, [medewerkerId]);

  useEffect(() => { laden(); }, [laden]);

  const upload = async (e) => {
    const input = e.target;
    const bestanden = Array.from(input.files || []);
    if (bestanden.length === 0) { input.value = ""; return; }
    const fd = new FormData();
    fd.append("medewerker_id", medewerkerId);
    bestanden.forEach((f) => fd.append("files", f));
    setBezig(true);
    try {
      const res = await axios.post(`${API}/documenten`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const nieuw = res.data.documenten || [];
      const herkend = nieuw.filter((d) => d.ai_gelukt).length;
      toast.success(`${nieuw.length} document(en) bewaard${herkend ? ` · ${herkend} door AI herkend` : ""}`);
      laden();
      onAiGegevens?.(nieuw);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Uploaden mislukt");
    } finally {
      setBezig(false);
      input.value = "";
    }
  };

  const wijzig = async (id, velden) => {
    try {
      const res = await axios.put(`${API}/documenten/${id}`, velden);
      setDocs((d) => d.map((x) => (x.id === id ? res.data : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Opslaan mislukt");
    }
  };

  const herlees = async (id) => {
    setBezig(true);
    try {
      const res = await axios.post(`${API}/documenten/${id}/ai-herlezen`);
      setDocs((d) => d.map((x) => (x.id === id ? res.data : x)));
      toast.success("AI heeft het document opnieuw gelezen");
      onAiGegevens?.([res.data]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI-herlezen mislukt");
    } finally {
      setBezig(false);
    }
  };

  const verwijder = async (id, titel) => {
    if (!window.confirm(`"${titel}" verwijderen?`)) return;
    try {
      await axios.delete(`${API}/documenten/${id}`);
      setDocs((d) => d.filter((x) => x.id !== id));
      toast.success("Document verwijderd");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  if (!medewerkerId) {
    return (
      <p className="text-xs text-muted-foreground italic py-2">
        Bewaar de medewerker eerst, daarna kan je documenten toevoegen.
      </p>
    );
  }

  return (
    <div className="space-y-4" data-testid="documentenkluis">
      <div>
        <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
          Ingelezen kaarten
        </Label>
        <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
          <FotoSlot medewerkerId={medewerkerId} soort="eid" kant="voor" aanwezig={!!form.eid_foto_voor} onVerwijderd={onFotoVerwijderd} />
          <FotoSlot medewerkerId={medewerkerId} soort="eid" kant="achter" aanwezig={!!form.eid_foto_achter} onVerwijderd={onFotoVerwijderd} />
          <FotoSlot medewerkerId={medewerkerId} soort="rijbewijs" kant="voor" aanwezig={!!form.rijbewijs_foto_voor} onVerwijderd={onFotoVerwijderd} />
          <FotoSlot medewerkerId={medewerkerId} soort="rijbewijs" kant="achter" aanwezig={!!form.rijbewijs_foto_achter} onVerwijderd={onFotoVerwijderd} />
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">
          Vervangen? Gebruik "Inlezen ID" of "Inlezen rijbewijs" hierboven — de AI vult de velden opnieuw in.
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between gap-2">
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">
            Documenten ({docs.length})
          </Label>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => cameraRef.current?.click()}
              disabled={bezig}
              className="text-[10px] font-bold uppercase tracking-wider text-[#002FA7] flex items-center gap-1 px-2 py-1 border border-[#002FA7] rounded-sm hover:bg-[#EAF1FF] disabled:opacity-50"
              data-testid="doc-camera-btn"
            >
              <Camera size={13} weight="bold" /> Foto
            </button>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={bezig}
              className="text-[10px] font-bold uppercase tracking-wider text-[#002FA7] flex items-center gap-1 px-2 py-1 border border-[#002FA7] rounded-sm hover:bg-[#EAF1FF] disabled:opacity-50"
              data-testid="doc-file-btn"
            >
              <FilePlus size={13} weight="bold" /> Bestand
            </button>
          </div>
        </div>

        <input ref={cameraRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={upload} data-testid="doc-camera-input" />
        <input ref={fileRef} type="file" accept="image/*,application/pdf,.heic,.heif" multiple className="hidden" onChange={upload} data-testid="doc-file-input" />

        {bezig && <p className="text-xs text-[#002FA7] mt-2 flex items-center gap-1" data-testid="doc-bezig"><Sparkle size={13} weight="fill" /> AI leest het document...</p>}

        {docs.length === 0 ? (
          <p className="text-xs text-muted-foreground italic py-2" data-testid="doc-leeg">
            Nog geen documenten. Neem een foto of kies een bestand — de AI herkent het type en de vervaldatum.
          </p>
        ) : (
          <div className="space-y-1.5 mt-2 max-h-[420px] overflow-y-auto pr-1">
            {docs.map((d) => (
              <div key={d.id} className="border border-border rounded-sm p-2 bg-muted/20" data-testid={`doc-${d.id}`}>
                <div className="flex items-start gap-2">
                  {d.mime?.startsWith("image/") && (
                    <a href={metFirma(`${API}/documenten/${d.id}/bestand`)} target="_blank" rel="noreferrer" className="shrink-0" title="Openen">
                      <img
                        src={metFirma(`${API}/documenten/${d.id}/bestand`)}
                        alt={d.titel}
                        className="h-12 w-16 object-cover rounded-sm border border-border hover:border-[#002FA7]"
                        data-testid={`doc-thumb-${d.id}`}
                      />
                    </a>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-semibold truncate">{d.titel}</span>
                      <GeldigheidBadge datum={d.geldig_tot} />
                    </div>
                    {d.ai_samenvatting && <p className="text-[10px] text-muted-foreground mt-0.5">{d.ai_samenvatting}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {(d.geldig_tot || d.nummer) && onAiGegevens && (
                      <button
                        type="button"
                        onClick={() => onAiGegevens([d])}
                        className="text-[#15803D] hover:opacity-70 p-1"
                        title="Datum/nummer overnemen in de velden hierboven"
                        data-testid={`doc-overnemen-${d.id}`}
                      >
                        <DownloadSimple size={14} weight="bold" />
                      </button>
                    )}
                    <a href={metFirma(`${API}/documenten/${d.id}/bestand`)} target="_blank" rel="noreferrer" className="text-[#002FA7] hover:opacity-70 p-1" title="Openen" data-testid={`doc-open-${d.id}`}>
                      <ArrowSquareOut size={14} weight="bold" />
                    </a>
                    <button type="button" onClick={() => herlees(d.id)} className="text-[#002FA7] hover:opacity-70 p-1" title="AI opnieuw laten lezen" data-testid={`doc-rescan-${d.id}`}>
                      <ArrowClockwise size={14} weight="bold" />
                    </button>
                    <button type="button" onClick={() => verwijder(d.id, d.titel)} className="text-[#DC2626] hover:opacity-70 p-1" title="Verwijderen" data-testid={`doc-del-${d.id}`}>
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                  <select
                    value={d.type}
                    onChange={(e) => wijzig(d.id, { type: e.target.value })}
                    className="h-8 text-[11px] rounded-sm border border-border bg-white px-1"
                    data-testid={`doc-type-${d.id}`}
                  >
                    {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <Input
                    defaultValue={d.titel}
                    onBlur={(e) => e.target.value !== d.titel && wijzig(d.id, { titel: e.target.value })}
                    placeholder="Titel"
                    className="h-8 text-[11px] rounded-sm"
                    data-testid={`doc-titel-${d.id}`}
                  />
                  <Input
                    type="date"
                    defaultValue={d.geldig_tot || ""}
                    onChange={(e) => wijzig(d.id, { geldig_tot: e.target.value })}
                    className="h-8 text-[11px] rounded-sm"
                    title="Geldig tot"
                    data-testid={`doc-datum-${d.id}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
